import React, { useRef, useState, useEffect } from "react";
import { Camera, RotateCcw, Check, X, MapPin, Clock } from "lucide-react";

const CameraCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [location, setLocation] = useState({
    lat: null,
    lng: null,
    error: null,
  });

  useEffect(() => {
    const startCamera = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (videoRef.current) videoRef.current.srcObject = s;
        setStream(s);
      } catch (err) {
        alert("Camera access denied. Please enable permissions.");
      }
    };

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          error: null,
        }),
      () => setLocation({ ...location, error: "Location unavailable" }),
    );

    startCamera();
    return () => stopCamera();
  }, []);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  };

const handleCapture = async () => {
  const video = videoRef.current;
  const canvas = canvasRef.current;

  if (!video || !canvas || video.videoWidth === 0) {
    alert("Camera not available.");
    return;
  }

  const context = canvas.getContext('2d');

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  const istTime = new Date().toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

  const barHeight = 90;

  context.fillStyle = "rgba(0, 0, 0, 0.4)";
  context.fillRect(0, canvas.height - barHeight, canvas.width, barHeight);

  const padding = 25;
  const labelColor = "#00D1FF";
  const valueColor = "#FFFFFF";

  const drawText = (label, value, x, y) => {
    context.shadowColor = "rgba(0,0,0,0.5)";
    context.shadowBlur = 4;

    context.font = "bold 16px sans-serif";
    context.fillStyle = labelColor;
    context.fillText(label, x, y);

    const labelWidth = context.measureText(label).width;
    context.font = "16px sans-serif";
    context.fillStyle = valueColor;
    context.fillText(value, x + labelWidth + 8, y);
  };

  drawText("TIMESTAMP:", istTime, padding, canvas.height - 55);

  const lat = location.lat ? location.lat.toFixed(6) : "Fetching...";
  const lng = location.lng ? location.lng.toFixed(6) : "Fetching...";

  drawText("LAT:", lat, padding, canvas.height - 25);
  drawText("LNG:", lng, padding + 200, canvas.height - 25);

  // 🔥 Compression
  const TARGET_SIZE = 300 * 1024;
  let quality = 0.9;
  let blob = null;

  do {
    blob = await new Promise(resolve =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );

    if (!blob) {
      alert("Image processing failed.");
      return;
    }

    quality -= 0.05;

  } while (blob.size > TARGET_SIZE && quality > 0.4);

  const compressedUrl = URL.createObjectURL(blob);
  setCapturedImage(compressedUrl);
  stopCamera();
};

  const handleConfirm = async () => {
    const response = await fetch(capturedImage);
    const blob = await response.blob();

    const file = new File([blob], `survey_${Date.now()}.jpg`, {
      type: "image/jpeg",
      lastModified: new Date().getTime(),
    });

    onCapture(file);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center">
      <div className="relative w-full h-full max-w-lg flex flex-col">
        {/* Header */}
        <div className="absolute top-0 w-full p-4 flex justify-between items-center z-10 bg-gradient-to-b from-black/70 to-transparent">
          <button
            onClick={onClose}
            className="text-white p-2 bg-black/20 rounded-full"
          >
            <X size={24} />
          </button>
          <div className="text-white text-xs text-right">
            <p className="flex items-center justify-end">
              <Clock size={12} className="mr-1" /> IST Captured
            </p>
            <p className="flex items-center justify-end">
              <MapPin size={12} className="mr-1" />{" "}
              {location.lat ? "GPS Locked" : "GPS Pending"}
            </p>
          </div>
        </div>

        {/* Viewport */}
        <div className="flex-1 flex items-center justify-center bg-neutral-900">
          {!capturedImage ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-auto max-h-full object-contain"
            />
          ) : (
            <img
              src={capturedImage}
              alt="Captured"
              className="w-full h-auto max-h-full object-contain"
            />
          )}
        </div>

        {/* Hidden Canvas */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Footer Controls */}
        <div className="p-8 bg-black flex justify-around items-center">
          {!capturedImage ? (
            <button
              onClick={handleCapture}
              className="w-20 h-20 bg-white rounded-full flex items-center justify-center border-4 border-gray-400 active:scale-95 transition"
            >
              <div className="w-16 h-16 bg-white rounded-full border-2 border-black"></div>
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  setCapturedImage(null);
                  window.location.reload();
                }}
                className="flex flex-col items-center text-white"
              >
                <div className="p-4 bg-gray-800 rounded-full mb-2">
                  <RotateCcw />
                </div>
                <span className="text-xs">Retake</span>
              </button>
              <button
                onClick={handleConfirm}
                className="flex flex-col items-center text-white"
              >
                <div className="p-4 bg-green-600 rounded-full mb-2">
                  <Check />
                </div>
                <span className="text-xs">Confirm</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
